'use client';

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, EnrichingCodesInfo, PulseButton, Typography } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import storage from 'local-storage-fallback';
import {
  faPlus,
  faFolderBookmark,
  faCaretDown,
  faBookmark,
} from '@fortawesome/pro-solid-svg-icons';
import { SavedVolumeType } from 'src/hooks/savedVolume/types';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { faGlobe } from '@fortawesome/pro-regular-svg-icons';
import { useNotification } from 'src/context/notification';
import { useEncounters } from 'src/context/providers/EncountersProvider';
import { useLogging } from 'src/context/logging';
import { MANAGE_VOLUME_ACTIVE_TAB_KEY } from 'src/components/Encounters/components/ManageVolumeDrawer/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { initialEncountersState, EncountersActionTypes } from 'src/context/reducers/encountersReducer';
import {
  DeleteCodeSetDialog,
  DeleteEncounterDialog,
  DeleteAISearchDialog,
  RenameAISearchDialog,
} from 'src/components/Encounters';
import { exists } from 'src/utils/exists/exists';
import { useReactivateProviderCohortMutation } from 'src/api/redux/providerCohorts';
import VolumeSection from './components/VolumeSection';
import { useSavedVolumePartitions } from 'src/components/CodeSetSelector/hooks/useSavedVolumePartitions';
import { useVolumeDragAndDrop } from 'src/components/CodeSetSelector/hooks/useVolumeDragAndDrop';

interface DropdownProps {
  onOpenEncounters?: () => void;
  showActions?: boolean;
  location: string;
}

const SavedVolumesDropdown: React.FC<DropdownProps> = ({
  onOpenEncounters,
  showActions = false,
  location,
}) => {
  const { dataCoverage: dataCoverageFlag } = useFlags();

  // Accordion indices
  const companyIndex = 0;
  const personalIndex = 1;

  // Dropdown UI state
  const [isOpen, setIsOpen] = useState(false);
  const [openAccordion, setOpenAccordion] = useState<number | null>(companyIndex);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  // Delete dialog state
  const [isDeleteEncounterDialogOpen, setIsDeleteEncounterDialogOpen] =
    useState(false);
  const [isDeleteFilterSetDialogOpen, setIsDeleteFilterSetDialogOpen] =
    useState(false);
  const [isDeleteAISearchDialogOpen, setIsDeleteAISearchDialogOpen] =
    useState(false);
  const [isRenameAISearchDialogOpen, setIsRenameAISearchDialogOpen] =
    useState(false);
  const [volumeToRename, setVolumeToRename] = useState<{
    id: number;
    label: string;
  } | null>(null);
  const [volumeToDelete, setVolumeToDelete] = useState<{
    id: number;
    label: string;
    volumeType?: 'cohort' | 'filterSet' | 'advancedSearch';
  } | null>(null);

  const { setNotification } = useNotification();
  const log = useLogging();
  const { dispatch } = useEncounters();
  const { openDrawer } = useDrawer();
  const [reactivateProviderCohort] = useReactivateProviderCohortMutation();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Extracted hooks
  const {
    personalVolumes,
    companyVolumes,
    personalFolders,
    companyFolders,
    isLoading,
    error,
    refetch,
    allCodeSets,
    cohortsData,
    advancedSearchesData,
    accountFoldersData,
    mutateAccountFolders,
    getFolderVolumes,
    currentCodeSet,
    currentProviderCohort,
    appliedSavedVolume,
    setSavedVolume,
    handleVolumeDeleted,
    renameAppliedAdvancedSearch,
  } = useSavedVolumePartitions(location);

  // Sync appliedSavedVolume when its cohort status changes. Kept out of
  // useSavedVolumePartitions because the Manage Code Sets drawer also calls that
  // hook and stays mounted, so a second sync would refetch Discovery twice.
  useEffect(() => {
    if (!appliedSavedVolume || appliedSavedVolume.volumeType !== 'cohort') {
      return;
    }

    const freshCohort = cohortsData?.results?.find(
      (cohort) => cohort.id === appliedSavedVolume.id
    );

    if (freshCohort && freshCohort.status !== appliedSavedVolume.status) {
      setSavedVolume({
        ...appliedSavedVolume,
        status: freshCohort.status,
      });
    }
  }, [cohortsData, appliedSavedVolume, setSavedVolume]);

  const {
    isDraggingRef,
    dragOverSection,
    setDragOverSection,
    handleDragStart,
    handleDragEnd,
    handleDrop,
    handleSectionDrop,
    handleAccordionDragOver,
    handleAccordionDragLeave,
    cleanupAllScrollIntervals,
  } = useVolumeDragAndDrop({
    cohortsData,
    allCodeSets,
    advancedSearchesData,
    accountFoldersData,
    mutateAccountFolders,
  });

  // Whether anything at all rendered — a failure alongside results is partial,
  // not a broken dropdown.
  const hasAnyVolumes =
    personalVolumes.length > 0 ||
    companyVolumes.length > 0 ||
    personalFolders.length > 0 ||
    companyFolders.length > 0;

  // Data coverage info
  const dataCoverage = currentCodeSet?.detailed_filters?.flatMap(
    (filter) =>
      filter.detailed_values?.map((value) => value.vrdc_enriched) ?? []
  );

  // Compute if any dialog is open
  const isAnyDialogOpen =
    isDeleteEncounterDialogOpen || isDeleteFilterSetDialogOpen || isDeleteAISearchDialogOpen || isRenameAISearchDialogOpen;

  // Position helpers
  const updatePosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  };

  // Click outside handler
  const handleClickOutside = (event: MouseEvent | PointerEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      triggerRef.current &&
      !triggerRef.current.contains(event.target as Node) &&
      !isAnyDialogOpen
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('pointerdown', handleClickOutside);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
    } else {
      document.removeEventListener('pointerdown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    }
    return () => {
      document.removeEventListener('pointerdown', handleClickOutside);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, isAnyDialogOpen]);

  // Clean up intervals when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      cleanupAllScrollIntervals();
    }
  }, [isOpen, cleanupAllScrollIntervals]);

  // Event handlers
  const toggleDropdown = () => {
    if (!isOpen) {
      updatePosition();
      // Nothing retries a rejected RTK Query cache entry, so a transient
      // failure would otherwise persist for the whole session. Reopening the
      // dropdown is the natural moment to try again.
      if (error) refetch();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index);
  };

  const handleSelect = (volume: SavedVolumeType) => {
    setSavedVolume(volume);
    setIsOpen(false);
  };

  const handleClearSelection = () => {
    setSavedVolume(null);
    setIsOpen(false);
  };

  const handleCreateNew = (e: React.MouseEvent) => {
    e.stopPropagation();

    dispatch({
      type: EncountersActionTypes.RESET_ENCOUNTERS_STATE,
      payload: initialEncountersState,
    });

    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_ID,
      payload: { volumeId: null },
    });

    setIsOpen(false);
    onOpenEncounters?.();
  };

  const handleEditClick = (
    e: React.MouseEvent,
    volume: { id: number; label: string; volumeType?: string }
  ) => {
    e.stopPropagation();

    const type = volume.volumeType || 'cohort';

    // AI searches use a lightweight rename dialog instead of the full edit drawer
    if (type === 'advancedSearch' || type === 'aiSearch') {
      setVolumeToRename({ id: volume.id, label: volume.label ?? '' });
      setIsRenameAISearchDialogOpen(true);
      return;
    }

    setIsOpen(false);

    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_ID,
      payload: { volumeId: volume.id.toString() },
    });

    dispatch({
      type: EncountersActionTypes.SET_EDITING_VOLUME_TYPE,
      payload: { volumeType: type },
    });

    dispatch({
      type: EncountersActionTypes.SET_EDITING_LOCATION,
      payload: { location },
    });

    if (type === 'filterSet') {
      openDrawer('editCodeSetsDrawer');
    } else {
      openDrawer('editEncountersDrawer');
    }
  };

  const handleDeleteClick = (
    e: React.MouseEvent,
    volume: { id: number; label: string; volumeType?: 'cohort' | 'filterSet' | 'advancedSearch' }
  ) => {
    e.stopPropagation();
    setVolumeToDelete(volume);

    if (volume.volumeType === 'filterSet') {
      setIsDeleteFilterSetDialogOpen(true);
    } else if (volume.volumeType === 'advancedSearch') {
      setIsDeleteAISearchDialogOpen(true);
    } else {
      setIsDeleteEncounterDialogOpen(true);
    }
  };

  const handleDeleteSuccess = () => handleVolumeDeleted(volumeToDelete);

  const handleReactivateClick = async (
    e: React.MouseEvent,
    volume: { id: number; label: string }
  ) => {
    e.stopPropagation();
    try {
      await reactivateProviderCohort(volume.id.toString()).unwrap();
      setNotification({
        title: 'Success',
        message: `${volume.label} reactivated`,
        type: 'success',
      });
    } catch (error) {
      log.exception(error, { tags: { context: 'reactivate-cohort' } });
      setNotification({
        title: 'Error',
        message: 'Failed to reactivate code set',
        type: 'error',
      });
    }
  };

  const handleOpenManageDrawer = (
    e: React.MouseEvent,
    type: 'PERSONAL' | 'COMPANY'
  ) => {
    e.stopPropagation();
    setIsOpen(false);

    storage.setItem(MANAGE_VOLUME_ACTIVE_TAB_KEY, type);
    openDrawer('manageVolumeDrawer');
  };

  const isProcessingVolume =
    (appliedSavedVolume?.volumeType === 'cohort' ||
      appliedSavedVolume?.volumeType === 'advancedSearch') &&
    appliedSavedVolume?.status === 'PROCESSING';

  return (
    <div className="relative w-full">
      {/* Dropdown Trigger */}
      <button
        type="button"
        ref={triggerRef}
        className="w-full px-4 h-9 bg-white flex items-center justify-between border border-neutral-300 rounded-full cursor-pointer hover:bg-neutral-50"
        onClick={toggleDropdown}
        data-testid="volume-selector-trigger"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {appliedSavedVolume ? (
            <Icon
              icon={faBookmark}
              className="w-4 h-4 text-neutral-600"
              size="sm"
            />
          ) : (
            <Icon icon={faGlobe} className="w-4 h-4 text-neutral-600" />
          )}
          {appliedSavedVolume ? (
            isProcessingVolume ? (
              <div className="flex items-center min-w-0">
                <span className="text-sm text-neutral-800 truncate">
                  {appliedSavedVolume.label} -
                </span>
                <span className="text-sm text-orange-500 italic ml-1 flex-shrink-0">
                  In Progress
                </span>
              </div>
            ) : (
              <>
                <Typography className="text-sm text-neutral-800 truncate font-semibold">
                  {appliedSavedVolume.label}
                </Typography>
                {appliedSavedVolume.volumeType === 'advancedSearch' && (
                  <Typography className="text-xs text-tertiary flex-shrink-0">
                    AI Search
                  </Typography>
                )}
                {appliedSavedVolume && dataCoverageFlag && (
                  <EnrichingCodesInfo
                    dataCoverage={dataCoverage}
                    iconOnly
                    isEncounter={exists(currentProviderCohort)}
                  />
                )}
              </>
            )
          ) : (
            <Typography className="text-sm text-neutral-800 truncate font-semibold">
              Search without Codes
            </Typography>
          )}
        </div>
        <Icon icon={faCaretDown} className="text-neutral-600 w-4 h-4 pl-1" />
      </button>

      {/* Dropdown Menu */}
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`fixed bg-white border border-neutral-300 rounded-lg shadow-lg overflow-hidden z-[9999] !w-72 ${
              dropdownPosition.top === 0 && dropdownPosition.left === 0
                ? 'opacity-0 pointer-events-none'
                : 'opacity-100'
            }`}
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              minHeight: 'auto',
              maxHeight: `calc(100vh - ${dropdownPosition.top}px - 16px)`,
              overflowY: 'auto',
            }}
            data-testid="volume-selector-dropdown"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-neutral-300 shrink-0">
              <div className="flex items-center gap-2">
                <Icon
                  icon={faFolderBookmark}
                  className="w-4 h-4 text-neutral-600"
                />
                <Typography className="text-sm font-bold text-neutral-800">
                  Code Sets
                </Typography>
              </div>

              {showActions && (
                <button
                  type="button"
                  className="flex items-center gap-1.5 cursor-pointer"
                  onClick={handleCreateNew}
                  data-testid="volume-selector-new-button"
                >
                  <Icon icon={faPlus} className="w-3 h-3 text-sky-900" />
                  <Typography className="text-sm font-bold text-sky-900">
                    New
                  </Typography>
                </button>
              )}
            </div>

            {/* Search without Codes */}
            <button
              type="button"
              className="w-full px-4 py-2 hover:bg-neutral-100 cursor-pointer flex items-center gap-2 text-left"
              onClick={handleClearSelection}
              data-testid="volume-selector-any-option"
            >
              <Icon icon={faGlobe} className="w-4 h-4 text-neutral-600" />
              <Typography className="text-sm text-neutral-800 font-semibold">
                Search without Codes
              </Typography>
            </button>

            {/* Loading State */}
            {isLoading && (
              <div className="px-4 py-4 flex justify-center items-center">
                <Typography className="text-sm text-neutral-500">
                  Loading volumes...
                </Typography>
              </div>
            )}

            {/* Company Section */}
            {!isLoading && (
              <VolumeSection
                label="Company"
                accordionIndex={companyIndex}
                openAccordion={openAccordion}
                onToggleAccordion={toggleAccordion}
                volumes={companyVolumes}
                folders={companyFolders}
                appliedSavedVolume={appliedSavedVolume}
                showActions={showActions}
                location={location}
                getFolderVolumes={getFolderVolumes}
                isDraggingRef={isDraggingRef}
                dragOverSection={dragOverSection}
                sectionKey="company"
                onSectionDragOver={handleAccordionDragOver}
                onSectionDragLeave={handleAccordionDragLeave}
                onSectionDrop={handleSectionDrop}
                onFolderDrop={handleDrop}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                setDragOverSection={setDragOverSection}
                onSelect={handleSelect}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onReactivate={handleReactivateClick}
                onManage={handleOpenManageDrawer}
              />
            )}

            {/* Personal Section */}
            {!isLoading && (
              <VolumeSection
                label="Personal"
                accordionIndex={personalIndex}
                openAccordion={openAccordion}
                onToggleAccordion={toggleAccordion}
                volumes={personalVolumes}
                folders={personalFolders}
                appliedSavedVolume={appliedSavedVolume}
                showActions={showActions}
                location={location}
                getFolderVolumes={getFolderVolumes}
                isDraggingRef={isDraggingRef}
                dragOverSection={dragOverSection}
                sectionKey="personal"
                onSectionDragOver={handleAccordionDragOver}
                onSectionDragLeave={handleAccordionDragLeave}
                onSectionDrop={handleSectionDrop}
                onFolderDrop={handleDrop}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                setDragOverSection={setDragOverSection}
                onSelect={handleSelect}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onReactivate={handleReactivateClick}
                onManage={handleOpenManageDrawer}
              />
            )}

            {/* No Volumes State */}
            {!isLoading && !error && !hasAnyVolumes && (
              <div className="px-4 py-4 flex justify-center items-center">
                <Typography className="text-sm text-neutral-500">
                  No code sets found
                </Typography>
              </div>
            )}

            {/* Error State — rendered after the sections, and scoped down to a
                partial failure when some volumes did load, so a single failed
                request never reads as "your volumes are broken". */}
            {!isLoading && error && (
              <div
                role="alert"
                className="px-4 py-3 flex flex-wrap justify-center items-center gap-2 border-t border-neutral-200"
                data-testid="volume-selector-error"
              >
                <Typography className="text-sm text-red-500">
                  {hasAnyVolumes
                    ? "Some volumes couldn't be loaded"
                    : 'Error loading volumes'}
                </Typography>
                <PulseButton
                  variant="plain"
                  size="xs"
                  onClick={refetch}
                  aria-label="Retry loading volumes"
                  data-testid="volume-selector-error-retry"
                >
                  Retry
                </PulseButton>
              </div>
            )}
          </div>,
          document.body
        )}

      {/* Delete Dialogs */}
      <DeleteEncounterDialog
        isOpen={isDeleteEncounterDialogOpen}
        onClose={() => {
          setIsDeleteEncounterDialogOpen(false);
          setVolumeToDelete(null);
        }}
        volumeToDelete={
          volumeToDelete && volumeToDelete.volumeType !== 'filterSet'
            ? volumeToDelete
            : null
        }
        onDeleteSuccess={handleDeleteSuccess}
      />

      <DeleteCodeSetDialog
        isOpen={isDeleteFilterSetDialogOpen}
        onClose={() => {
          setIsDeleteFilterSetDialogOpen(false);
          setVolumeToDelete(null);
        }}
        volumeToDelete={
          volumeToDelete && volumeToDelete.volumeType === 'filterSet'
            ? {
                id: volumeToDelete.id,
                label: volumeToDelete.label,
                volumeType: 'filterSet',
              }
            : null
        }
        onDeleteSuccess={handleDeleteSuccess}
      />

      <DeleteAISearchDialog
        isOpen={isDeleteAISearchDialogOpen}
        onClose={() => {
          setIsDeleteAISearchDialogOpen(false);
          setVolumeToDelete(null);
        }}
        volumeToDelete={
          volumeToDelete && volumeToDelete.volumeType === 'advancedSearch'
            ? volumeToDelete
            : null
        }
        onDeleteSuccess={handleDeleteSuccess}
      />

      {isRenameAISearchDialogOpen && volumeToRename && (
        <RenameAISearchDialog
          isOpen
          onClose={() => {
            setIsRenameAISearchDialogOpen(false);
            setVolumeToRename(null);
          }}
          volumeToRename={volumeToRename}
          onRenameSuccess={renameAppliedAdvancedSearch}
        />
      )}
    </div>
  );
};

export default SavedVolumesDropdown;
